/*
 * Healix Set Gallery — client-side component-media swap for "set mode" PDPs.
 *
 * Set-mode PDPs (ghost products with template_suffix "set" + custom.set_top /
 * custom.set_bottom metafields) render a unified media list server-side:
 *   ghost-hero media → top component media → pant component media.
 * Each media item carries data-set-component="hero|top|pant". Hero items are
 * colour-agnostic set shots and NEVER change. When the shopper picks a colour
 * in the concurrent "set buy" block, healix-set.js dispatches
 *   document -> 'healix:set:color-change'
 *   detail = { slug, top: { handle, imageUrl }, pant: { handle, imageUrl } }
 * (fired on every selection INCLUDING the automatic first selection at boot).
 *
 * This module rebuilds the REPLACEABLE (top/pant) media items in every layout
 * container from the newly-selected sibling products' media, keeping hero items
 * untouched and first, then re-syncs the two JS layers that scraped the DOM at
 * init: window ProductMediaGallery (via its additive refresh()) and the
 * fullscreen viewer (via the additive gallery.refreshMediaViewer()).
 *
 * House style: ES5-leaning IIFE, var/function, no build step, no dependencies.
 * Non-image payload media degrade to their preview image (see LIMITATION below).
 */
(function () {
  'use strict';

  // Stale-guard: a rapid second colour change bumps the token so the first
  // fetch's response is discarded when it finally resolves.
  var requestToken = 0;

  function getGallery() {
    return document.querySelector('.product-media-gallery[data-set-mode="true"]');
  }

  // ── URL helpers ───────────────────────────────────────────────────────────
  // The /products/<handle>.js payload gives CDN image URLs (media.preview_image
  // .src or media.src). The JS scrapers regex-replace width=\d+, so every URL we
  // emit MUST carry a width param. Shopify CDN URLs accept ?width=.
  function withWidth(url, w) {
    if (!url) return '';
    // Normalise protocol-relative URLs the .js payload sometimes returns.
    if (url.indexOf('//') === 0) url = 'https:' + url;
    if (/([?&])width=\d+/.test(url)) {
      return url.replace(/([?&])width=\d+/, '$1width=' + w);
    }
    return url + (url.indexOf('?') === -1 ? '?' : '&') + 'width=' + w;
  }

  // Pull the best source URL out of a /products/<handle>.js media entry.
  // Image entries expose .src; video/model entries expose .preview_image.src.
  function mediaSrc(m) {
    if (!m) return '';
    if (m.src) return m.src;
    if (m.preview_image && m.preview_image.src) return m.preview_image.src;
    return '';
  }

  // ── Per-container markup builders ─────────────────────────────────────────
  // Each builder reproduces the Liquid markup contract for ONE layout item.
  // `m` is a .js media entry, `index` is its final unified 0..n index.

  function imgAttrs(m, widths, mainWidth) {
    var base = mediaSrc(m);
    var src = withWidth(base, mainWidth);
    var srcset = widths
      .map(function (w) { return withWidth(base, w) + ' ' + w + 'w'; })
      .join(', ');
    var lightbox = withWidth(base, 2400);
    var alt = (m.alt || '').replace(/"/g, '&quot;');
    return { src: src, srcset: srcset, lightbox: lightbox, alt: alt };
  }

  function setDataAttrs(el, m, index, component) {
    el.setAttribute('data-media-id', m.id);
    el.setAttribute('data-media-type', m.media_type || 'image');
    el.setAttribute('data-index', index);
    el.setAttribute('data-set-component', component);
  }

  // Mobile carousel item
  function buildCarouselItem(m, index, component) {
    var a = imgAttrs(m, [400, 600, 900], 900);
    var el = document.createElement('div');
    el.className = 'product-media-gallery__carousel-item';
    setDataAttrs(el, m, index, component);
    el.innerHTML =
      '<div class="product-media-gallery__image-wrapper">' +
        '<img src="' + a.src + '" srcset="' + a.srcset + '" sizes="100vw" alt="' + a.alt + '"' +
        ' loading="lazy" decoding="async"' +
        ' class="product-media-gallery__carousel-media product-media-gallery__image"' +
        ' data-lightbox-src="' + a.lightbox + '">' +
      '</div>';
    return el;
  }

  // Mobile collage grid item (offset:1 items) — same markup as collage grid.
  function buildMobileCollageItem(m, index, component) {
    var a = imgAttrs(m, [300, 450, 600], 600);
    var el = document.createElement('div');
    el.className = 'product-media-gallery__mobile-collage-item';
    setDataAttrs(el, m, index, component);
    el.innerHTML =
      '<div class="product-media-gallery__image-wrapper">' +
        '<img src="' + a.src + '" srcset="' + a.srcset + '" sizes="50vw" alt="' + a.alt + '"' +
        ' loading="lazy" class="product-media-gallery__image"' +
        ' data-lightbox-src="' + a.lightbox + '">' +
      '</div>';
    return el;
  }

  // Desktop collage grid item
  function buildCollageGridItem(m, index, component) {
    var a = imgAttrs(m, [400, 600, 800], 800);
    var el = document.createElement('div');
    el.className = 'product-media-gallery__grid-item';
    setDataAttrs(el, m, index, component);
    el.innerHTML =
      '<div class="product-media-gallery__image-wrapper">' +
        '<img src="' + a.src + '" srcset="' + a.srcset + '" sizes="(min-width: 990px) 25vw, 50vw"' +
        ' alt="' + a.alt + '" loading="lazy" class="product-media-gallery__image"' +
        ' data-lightbox-src="' + a.lightbox + '">' +
      '</div>';
    return el;
  }

  // Desktop stacked item
  function buildStackedItem(m, index, component) {
    var a = imgAttrs(m, [600, 900, 1200, 1800], 1200);
    var el = document.createElement('div');
    el.className = 'product-media-gallery__stacked-item';
    setDataAttrs(el, m, index, component);
    el.innerHTML =
      '<div class="product-media-gallery__image-wrapper">' +
        '<img src="' + a.src + '" srcset="' + a.srcset + '" sizes="(min-width: 990px) 50vw, 100vw"' +
        ' alt="' + a.alt + '" loading="lazy" decoding="async" class="product-media-gallery__image"' +
        ' data-lightbox-src="' + a.lightbox + '">' +
      '</div>';
    return el;
  }

  // Desktop thumbnail-strip / mobile-thumbnail button (200px thumb)
  function buildThumbnailButton(m, index, component) {
    var thumbSrc = withWidth(mediaSrc(m), 200);
    var alt = (m.alt || '').replace(/"/g, '&quot;');
    var el = document.createElement('button');
    el.type = 'button';
    el.className = 'product-media-gallery__thumbnail';
    el.setAttribute('data-media-id', m.id);
    el.setAttribute('data-index', index);
    el.setAttribute('data-set-component', component);
    el.setAttribute('aria-label', 'View image ' + (index + 1));
    el.innerHTML =
      '<img src="' + thumbSrc + '" alt="' + alt + '" loading="lazy" width="200" height="200"' +
      ' class="product-media-gallery__thumbnail-image">';
    return el;
  }

  // ── Container rebuild ─────────────────────────────────────────────────────
  // For a container, replace all data-set-component="top"|"pant" children with
  // freshly-built items from `topMedia` then `pantMedia`, preserving hero items
  // and their order (hero always first). Uses a builder fn for that container.
  function rebuildContainer(container, builder, topMedia, pantMedia, skipFirstTop) {
    if (!container) return;

    // Selector for replaceable items directly inside this container.
    var replaceable = container.querySelectorAll('[data-set-component="top"], [data-set-component="pant"]');
    if (!replaceable.length) {
      // Container has no replaceable items rendered (e.g. hero-only). Nothing to
      // rebuild; the reindex pass keeps its hero items consistent.
      return;
    }

    // Component items share a parent (the list container); new items append
    // after the hero items, which always lead. Order stays hero → top → pant.
    var parent = replaceable[0].parentNode;

    // Remove existing replaceable items.
    for (var i = 0; i < replaceable.length; i++) {
      replaceable[i].parentNode.removeChild(replaceable[i]);
    }

    // main+grid containers with NO hero show top[0] as the separate __main
    // element (offset:1 loop), so the grid must skip the first top item.
    var tops = skipFirstTop ? topMedia.slice(1) : topMedia;

    var idx = 0;
    var frag = document.createDocumentFragment();
    tops.forEach(function (m) {
      frag.appendChild(builder(m, idx, 'top'));
      idx++;
    });
    pantMedia.forEach(function (m) {
      frag.appendChild(builder(m, idx, 'pant'));
      idx++;
    });
    parent.appendChild(frag);
  }

  // Re-index the unified media list 0..n after a swap so the scrapers (which read
  // data-index) stay consistent. Each layout renders the SAME list (hero → top →
  // pant), so each independent scope is 0..n. We re-index by structural pattern
  // matching the Liquid exactly:
  //   - "full" scopes: all direct set-component items are 0..n (carousel,
  //     thumbnail strips, stacked).
  //   - "main+grid" scopes: a single main element is index 0, and a sibling grid
  //     container holds items 1..n (collage, mobile collage).
  //   - single-element scopes (featured/single/main-without-grid): index 0.
  function reindexSequential(items, start) {
    var seq = start;
    for (var i = 0; i < items.length; i++) {
      items[i].setAttribute('data-index', seq);
      seq++;
    }
    return seq;
  }

  function reindexFull(scope) {
    if (!scope) return;
    reindexSequential(scope.querySelectorAll('[data-set-component]'), 0);
  }

  // main element at 0, grid items (siblings) at 1..n.
  function reindexMainGrid(main, grid) {
    if (main) main.setAttribute('data-index', 0);
    if (grid) reindexSequential(grid.querySelectorAll('[data-set-component]'), 1);
  }

  // Special case: the collage/thumbnail/single "main/featured/single" element is
  // index 0 and is a HERO item when hero media exists; if there is NO hero media
  // the server rendered the FIRST component item as the main element. In that
  // case index 0 is a top item and must be rebuilt too. We handle the common
  // path (hero exists → main is hero → untouched). When hero-count is 0 we fall
  // back to rebuilding the main element as the first top item.
  function rebuildMainElement(gallery, topMedia, pantMedia, heroCount) {
    if (heroCount > 0) return; // main element is a hero shot — leave it.
    var first = topMedia[0] || pantMedia[0];
    if (!first) return;
    var component = topMedia.length ? 'top' : 'pant';

    var selectors = [
      '.product-media-gallery__main',
      '.product-media-gallery__featured',
      '.product-media-gallery__single',
      '.product-media-gallery__mobile-collage-main'
    ];
    selectors.forEach(function (sel) {
      var el = gallery.querySelector(sel);
      if (!el) return;
      var isFeatured = el.classList.contains('product-media-gallery__featured');
      var a = imgAttrs(first, [600, 900, 1200, 1800], 1200);
      el.setAttribute('data-media-id', first.id);
      el.setAttribute('data-media-type', first.media_type || 'image');
      el.setAttribute('data-index', 0);
      el.setAttribute('data-set-component', component);
      var img = el.querySelector('img');
      if (img) {
        img.src = a.src;
        img.srcset = a.srcset;
        img.alt = a.alt;
        img.setAttribute('data-lightbox-src', a.lightbox);
        if (isFeatured) img.classList.add('product-media-gallery__featured-image');
      }
    });
  }

  // ── Main swap ─────────────────────────────────────────────────────────────
  function swap(gallery, topMedia, pantMedia) {
    var heroCount = parseInt(gallery.getAttribute('data-set-hero-count'), 10) || 0;
    // When there is no ghost-hero media, the first top item is promoted to the
    // separate main/featured/single element (offset:1 in the collage grids), so
    // those grids must skip top[0].
    var skipFirstTopInGrid = heroCount === 0;

    // Desktop collage grid (offset:1 loop → grid items are index 1..n).
    rebuildContainer(
      gallery.querySelector('.product-media-gallery__grid'),
      buildCollageGridItem, topMedia, pantMedia, skipFirstTopInGrid
    );
    // Desktop stacked (all items in one flow — full list).
    rebuildContainer(
      gallery.querySelector('.product-media-gallery__stacked'),
      buildStackedItem, topMedia, pantMedia, false
    );
    // Desktop thumbnail strip (full list).
    rebuildContainer(
      gallery.querySelector('.product-media-gallery__desktop-thumbnails .product-media-gallery__thumbnails-scroll'),
      buildThumbnailButton, topMedia, pantMedia, false
    );
    // Mobile carousel (full list).
    rebuildContainer(
      gallery.querySelector('.product-media-gallery__carousel-slider'),
      buildCarouselItem, topMedia, pantMedia, false
    );
    // Mobile thumbnails (full list).
    rebuildContainer(
      gallery.querySelector('.product-media-gallery__mobile-thumbnails .product-media-gallery__thumbnails-scroll'),
      buildThumbnailButton, topMedia, pantMedia, false
    );
    // Mobile collage grid (offset:1 loop).
    rebuildContainer(
      gallery.querySelector('.product-media-gallery__mobile-collage-grid'),
      buildMobileCollageItem, topMedia, pantMedia, skipFirstTopInGrid
    );

    // Rebuild the single "main" element if it holds a component item (no hero).
    rebuildMainElement(gallery, topMedia, pantMedia, heroCount);

    // Re-index each independent media-list scope, matching the Liquid indexing.
    // Desktop collage: __main (0) + __grid (1..n).
    reindexMainGrid(
      gallery.querySelector('.product-media-gallery__main'),
      gallery.querySelector('.product-media-gallery__grid')
    );
    // Desktop thumbnail: __featured (0) + strip (0..n) are SEPARATE scopes.
    var featured = gallery.querySelector('.product-media-gallery__featured');
    if (featured) featured.setAttribute('data-index', 0);
    reindexFull(gallery.querySelector('.product-media-gallery__desktop-thumbnails .product-media-gallery__thumbnails-scroll'));
    // Desktop stacked: one full-list scope.
    reindexFull(gallery.querySelector('.product-media-gallery__stacked'));
    // Desktop single: single element at 0.
    var single = gallery.querySelector('.product-media-gallery__single');
    if (single) single.setAttribute('data-index', 0);
    // Mobile carousel: full-list scope.
    reindexFull(gallery.querySelector('.product-media-gallery__carousel-slider'));
    // Mobile thumbnails: full-list scope.
    reindexFull(gallery.querySelector('.product-media-gallery__mobile-thumbnails .product-media-gallery__thumbnails-scroll'));
    // Mobile collage: __mobile-collage-main (0) + __mobile-collage-grid (1..n).
    reindexMainGrid(
      gallery.querySelector('.product-media-gallery__mobile-collage-main'),
      gallery.querySelector('.product-media-gallery__mobile-collage-grid')
    );

    // ── Re-sync the JS layers ──
    // 1) ProductMediaGallery re-scrapes thumbnails + resets to slide 0.
    if (gallery.productMediaGalleryInstance &&
        typeof gallery.productMediaGalleryInstance.refresh === 'function') {
      gallery.productMediaGalleryInstance.refresh();
    }
    // 2) Fullscreen viewer rebuilds its captured media list + binds new nodes.
    if (typeof gallery.refreshMediaViewer === 'function') {
      gallery.refreshMediaViewer();
    }
  }

  // ── Media list from a /products/<handle>.js product ───────────────────────
  function mediaListOf(product) {
    if (!product) return [];
    if (product.media && product.media.length) return product.media;
    // Fallback: some payloads only carry `images`. Synthesise image entries so
    // the swap still shows something (rare — colourway products carry media).
    if (product.images && product.images.length) {
      return product.images.map(function (src, i) {
        return { id: 'img-' + product.id + '-' + i, media_type: 'image', src: src, alt: product.title || '' };
      });
    }
    return [];
  }

  // ── Preload the new featured image before swapping (avoid flash) ───────────
  function preload(url) {
    return new Promise(function (resolve) {
      if (!url) return resolve();
      var im = new Image();
      im.onload = resolve;
      im.onerror = resolve;
      im.src = url;
    });
  }

  // ── Fade helper (consistent with updateDesktopFeaturedImage's fade) ────────
  function fade(gallery, to) {
    var main = gallery.querySelector('.product-media-gallery__desktop');
    if (!main) return;
    main.style.transition = 'opacity 0.15s ease';
    main.style.opacity = to;
  }

  // ── Skeleton overlay on the swapped (top/pant) media while the new component
  // media preloads. Graceful, no layout jump (absolute overlay inside each
  // item's positioned wrapper), and torn down on the next call so a rapid second
  // colour change can't leave the first's overlay behind. Set-scoped class
  // (.hxset-media-skeleton) styled in assets/healix-set.css.
  function clearSkeletons(gallery) {
    gallery.querySelectorAll('.hxset-media-skeleton').forEach(function (n) {
      if (n.parentNode) n.parentNode.removeChild(n);
    });
  }

  function showSkeletons(gallery) {
    clearSkeletons(gallery);
    // Overlay every currently-visible replaceable media item + the promoted
    // main element (hero-count 0 case). Skip thumbnails (tiny, not worth it).
    var items = gallery.querySelectorAll(
      '.product-media-gallery__grid-item[data-set-component="top"],' +
      '.product-media-gallery__grid-item[data-set-component="pant"],' +
      '.product-media-gallery__stacked-item[data-set-component="top"],' +
      '.product-media-gallery__stacked-item[data-set-component="pant"],' +
      '.product-media-gallery__carousel-item[data-set-component="top"],' +
      '.product-media-gallery__carousel-item[data-set-component="pant"],' +
      '.product-media-gallery__mobile-collage-item[data-set-component="top"],' +
      '.product-media-gallery__mobile-collage-item[data-set-component="pant"],' +
      '.product-media-gallery__main[data-set-component="top"],' +
      '.product-media-gallery__main[data-set-component="pant"],' +
      '.product-media-gallery__featured[data-set-component="top"],' +
      '.product-media-gallery__featured[data-set-component="pant"],' +
      '.product-media-gallery__single[data-set-component="top"],' +
      '.product-media-gallery__single[data-set-component="pant"]'
    );
    items.forEach(function (item) {
      var host = item.querySelector('.product-media-gallery__image-wrapper') || item;
      // Ensure the host can anchor an absolutely-positioned overlay.
      var pos = window.getComputedStyle(host).position;
      if (pos === 'static') host.style.position = 'relative';
      var sk = document.createElement('span');
      sk.className = 'hxset__sk hxset-media-skeleton';
      host.appendChild(sk);
    });
  }

  // ── Event handler ─────────────────────────────────────────────────────────
  function onColorChange(e) {
    var gallery = getGallery();
    if (!gallery) return;

    var detail = e && e.detail;
    if (!detail || !detail.top || !detail.pant) return;

    var topHandle = detail.top.handle;
    var pantHandle = detail.pant.handle;
    if (!topHandle || !pantHandle) return;

    // First paint already correct: server rendered THIS exact pair. No-op.
    if (topHandle === gallery.getAttribute('data-set-top-handle') &&
        pantHandle === gallery.getAttribute('data-set-pant-handle')) {
      return;
    }

    if (!window.HealixColors || !window.HealixColors.product) return;

    var myToken = ++requestToken;

    Promise.all([
      window.HealixColors.product(topHandle),
      window.HealixColors.product(pantHandle)
    ]).then(function (results) {
      // Stale-guard: a newer colour change superseded this one.
      if (myToken !== requestToken) return;

      var topMedia = mediaListOf(results[0]);
      var pantMedia = mediaListOf(results[1]);
      if (!topMedia.length && !pantMedia.length) return;

      // Preload the first replaceable image (the one most likely to become the
      // featured/main image) before swapping to avoid a visible flash.
      var firstNew = topMedia[0] || pantMedia[0];
      var preloadUrl = firstNew ? withWidth(mediaSrc(firstNew), 1200) : '';

      fade(gallery, '0.4');
      // Shimmer overlay on the outgoing component media while the new media
      // preloads. showSkeletons() first clears any prior overlay, so a rapid
      // second colour change tears down the first's skeletons here.
      showSkeletons(gallery);
      preload(preloadUrl).then(function () {
        if (myToken !== requestToken) { fade(gallery, '1'); return; }
        // Skeletons on the OLD nodes are removed as those nodes are replaced by
        // swap(); clear defensively first (covers hero/main-element paths).
        clearSkeletons(gallery);
        swap(gallery, topMedia, pantMedia);
        // Update the gallery's server-rendered pair so a repeat of the same
        // colour short-circuits as a no-op next time.
        gallery.setAttribute('data-set-top-handle', topHandle);
        gallery.setAttribute('data-set-pant-handle', pantHandle);
        // Reset to slide 0 (refresh() already did, but ensure after re-index).
        if (gallery.productMediaGalleryInstance &&
            typeof gallery.productMediaGalleryInstance.goToSlide === 'function') {
          gallery.productMediaGalleryInstance.goToSlide(0);
        }
        fade(gallery, '1');
      });
    });
  }

  document.addEventListener('healix:set:color-change', onColorChange);
})();
